import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <div className="sticky top-0 z-50 bg-white border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img
                src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/473109dd943942010dc73e384d113dfa.png"
                alt="AK Energy"
                className="h-7 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 md:px-6 lg:px-10 py-10 md:py-14">
        <p className="text-xs text-foreground-400 uppercase tracking-wider mb-2">Last updated: July 2026</p>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">Privacy Policy</h1>

        <div className="prose prose-sm max-w-none text-foreground-700 space-y-6">
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">1. Introduction</h2>
            <p>
              AK Energy (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website, use our marketplace platform, or engage with our services. We comply with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).
            </p>
            <p>
              By accessing or using our website, you acknowledge that you have read and understood this Privacy Policy. If you do not agree with our practices, please discontinue use of our services.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">2. Information We Collect</h2>
            <p>We may collect the following types of personal information:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Contact Information:</strong> Full name, email address, phone number, and postal address when you submit an enquiry, request a quote, or create an account.</li>
              <li><strong>Business Information:</strong> Company name, ABN, business type, and industry details when you register as an installer, manufacturer, finance provider, or energy retailer.</li>
              <li><strong>Energy Usage Data:</strong> Information about your current energy consumption, system preferences, property type, and household size to provide tailored renewable energy recommendations.</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system, referring URLs, and browsing behaviour collected through cookies and similar technologies.</li>
              <li><strong>Communication Records:</strong> Records of correspondence including emails, contact form submissions, chat messages, and phone call notes.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">3. How We Collect Information</h2>
            <p>We collect information through the following methods:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Information you provide directly through our website forms (enquiry forms, newsletter sign-ups, and account registrations).</li>
              <li>Automated technologies including cookies, server logs, and analytics tools when you browse our website.</li>
              <li>Third-party sources such as publicly available databases, marketing partners, and social media platforms where you interact with our brand.</li>
              <li>Participant partners including installers, manufacturers, and energy retailers who may share relevant data with your consent.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">4. How We Use Your Information</h2>
            <p>We use your personal information for the following purposes:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>To connect you with relevant renewable energy installers, manufacturers, finance providers, and energy retailers through our marketplace.</li>
              <li>To respond to enquiries, provide quotes, and deliver customer support.</li>
              <li>To personalise your experience and present energy solutions tailored to your property and consumption patterns.</li>
              <li>To send marketing communications about renewable energy products, industry updates, and special offers (you may opt out at any time).</li>
              <li>To improve our website functionality, user experience, and service offerings through analytics and feedback.</li>
              <li>To comply with legal obligations, enforce our terms of service, and protect our rights and interests.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">5. Disclosure of Your Information</h2>
            <p>We may disclose your personal information to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li><strong>Marketplace Participants:</strong> Installers, manufacturers, finance providers, energy retailers, and VPP operators on our platform to facilitate quotes, installations, and service delivery.</li>
              <li><strong>Service Providers:</strong> Third-party vendors who assist with website hosting, data analytics, email delivery, payment processing, and customer support.</li>
              <li><strong>Legal and Regulatory Authorities:</strong> Where required by law, court order, or government regulation.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of all or part of our business assets.</li>
            </ul>
            <p className="mt-2">We do not sell your personal information to third parties for their independent marketing purposes.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">6. Data Storage and Security</h2>
            <p>
              We store personal information on secure servers located in Australia. We implement industry-standard security measures including encryption, firewalls, access controls, and regular security audits to protect your data from unauthorised access, alteration, disclosure, or destruction.
            </p>
            <p>
              While we take reasonable precautions, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security of your information.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">7. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfil the purposes outlined in this policy, or as required by Australian law. When your data is no longer needed, we will securely delete or anonymise it. Enquiry and transaction records are typically retained for a minimum of seven years to comply with tax and business record-keeping obligations.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">8. Your Rights</h2>
            <p>Under the Australian Privacy Principles, you have the right to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Access the personal information we hold about you.</li>
              <li>Request correction of inaccurate or incomplete personal information.</li>
              <li>Request deletion of your personal information (subject to legal retention requirements).</li>
              <li>Withdraw consent for marketing communications at any time.</li>
              <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) if you believe we have breached your privacy rights.</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, please contact us using the details provided in Section 10.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">9. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies, web beacons, and similar tracking technologies to enhance your browsing experience, analyse website traffic, and deliver relevant advertising. For detailed information about how we use cookies, please refer to our <Link to="/cookie-policy" className="text-primary-600 hover:text-primary-700 underline">Cookie Policy</Link>.
            </p>
            <p>
              You can manage your cookie preferences through your browser settings. Please note that disabling certain cookies may affect website functionality.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">10. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, wish to access or correct your personal information, or would like to make a complaint, please contact us:
            </p>
            <div className="mt-3 space-y-1 text-sm">
              <p><strong>Email:</strong> akenergyau@gmail.com</p>
              <p><strong>Phone:</strong> 1800 253 637</p>
              <p><strong>Post:</strong> Privacy Officer, AK Energy, GPO Box 1234, Sydney NSW 2001, Australia</p>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or service offerings. We will post the updated version on this page and update the &ldquo;Last updated&rdquo; date. We encourage you to review this policy periodically. Continued use of our website after any changes constitutes acceptance of the updated policy.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}